import { useCallback, useEffect, useRef, useState } from "react";

const START_OF_TURN = "<STARTOFTURN>";
const END_OF_TURN = "<ENDOFTURN>";

const MAX_RECONNECT_ATTEMPTS = 5;
const RECONNECT_DECAY = 1.5;
const MAX_RECONNECT_INTERVAL = 30000;

/**
 * Streams the AI-written story description over
 * `/ws/learning_objective_description/`.
 *
 * The socket opens once on mount and stays open — each "Generate with AI" click
 * sends a fresh payload. The backend cancels any in-flight stream when a second
 * payload arrives, so the buffer is reset on every send.
 *
 * Stream shape: `{"message": "<STARTOFTURN>"}` … chunks … `{"message": "<ENDOFTURN>"}`.
 * Everything between the markers is concatenated; `<ENDOFTURN>` means complete.
 * Failures arrive as `{"error": "…", "detail": "…"}` frames, not close codes.
 */
export const useObjectiveDescriptionSocket = ({
  wsURL,
  token,
  onStreamStart,
  onStream,
  onStreamEnd,
}) => {
  const socketRef = useRef(null);
  const chunksRef = useRef([]);
  const shouldReconnectRef = useRef(true);
  const reconnectAttemptsRef = useRef(0);
  const reconnectIntervalRef = useRef(1000);
  const reconnectTimerRef = useRef(null);

  const [isSocketReady, setIsSocketReady] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamError, setStreamError] = useState("");

  // Handlers live in a ref so the socket callbacks always see the latest
  // closures without having to tear the connection down and reconnect.
  const handlersRef = useRef({ onStreamStart, onStream, onStreamEnd });
  useEffect(() => {
    handlersRef.current = { onStreamStart, onStream, onStreamEnd };
  }, [onStreamStart, onStream, onStreamEnd]);

  const handleMessage = useCallback((event) => {
    let data;
    try {
      data = JSON.parse(event.data);
    } catch (error) {
      console.error("Objective description socket: unparseable frame", error);
      return;
    }

    // Error frames end the stream but leave the socket usable.
    if (data?.error) {
      console.error("Objective description socket error frame:", data);
      setStreamError(data.detail ? `${data.error} ${data.detail}` : data.error);
      setIsStreaming(false);
      chunksRef.current = [];
      return;
    }

    const message = data?.message;
    if (typeof message !== "string") return;

    if (message.includes(START_OF_TURN)) {
      chunksRef.current = [];
      setIsStreaming(true);
      handlersRef.current.onStreamStart?.();
    }

    const isComplete = message.includes(END_OF_TURN);
    // A chunk can carry a marker alongside real text — strip, keep the text.
    const text = message
      .split(START_OF_TURN)
      .join("")
      .split(END_OF_TURN)
      .join("");

    if (text) chunksRef.current.push(text);

    const streamed = chunksRef.current.join("");
    if (text) handlersRef.current.onStream?.(streamed);

    if (isComplete) {
      setIsStreaming(false);
      chunksRef.current = [];
      handlersRef.current.onStreamEnd?.(streamed);
    }
  }, []);

  // ── connection (opens once on mount, reconnects with backoff) ──────────────
  useEffect(() => {
    if (!token) return undefined;

    shouldReconnectRef.current = true;

    const connect = () => {
      const url = `${wsURL}ws/learning_objective_description/?token=${token}`;
      const socket = new WebSocket(url);
      socketRef.current = socket;

      socket.onopen = () => {
        setIsSocketReady(true);
        reconnectAttemptsRef.current = 0;
        reconnectIntervalRef.current = 1000;
        chunksRef.current = [];
      };

      socket.onmessage = handleMessage;

      socket.onclose = () => {
        setIsSocketReady(false);
        setIsStreaming(false);
        if (
          !shouldReconnectRef.current ||
          reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS
        ) {
          return;
        }
        reconnectTimerRef.current = setTimeout(() => {
          reconnectAttemptsRef.current += 1;
          reconnectIntervalRef.current = Math.min(
            reconnectIntervalRef.current * RECONNECT_DECAY,
            MAX_RECONNECT_INTERVAL
          );
          connect();
        }, reconnectIntervalRef.current);
      };

      socket.onerror = (error) => {
        console.error("Objective description socket error:", error);
        socket.close();
      };
    };

    connect();

    return () => {
      shouldReconnectRef.current = false;
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      const socket = socketRef.current;
      if (
        socket &&
        (socket.readyState === WebSocket.OPEN ||
          socket.readyState === WebSocket.CONNECTING)
      ) {
        socket.close();
      }
      socketRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsURL, token]);

  /**
   * Sends a payload and starts a fresh stream.
   * @returns {boolean} false when the socket is not connected
   */
  const requestDescription = useCallback((payload) => {
    const socket = socketRef.current;
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      setStreamError("Please try again after sometime.");
      return false;
    }

    // A second payload cancels whatever was streaming — start from a clean buffer.
    chunksRef.current = [];
    setStreamError("");
    setIsStreaming(true);
    socket.send(JSON.stringify(payload));
    return true;
  }, []);

  return {
    requestDescription,
    isSocketReady,
    isStreaming,
    streamError,
    setStreamError,
  };
};

export default useObjectiveDescriptionSocket;
